import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmt } from './arrShared';
import { DevOverlay } from '../../components/DevOverlay/DevOverlay';

const EMPTY_ARRAY = [];
const PALETTE = ['#1e5fa8', '#15803d', '#d97706', '#6d28d9', '#0f766e', '#b91c1c', '#ea580c', '#64748b'];
const EVENT_COLORS = { New: '#1e5fa8', Upsell: '#22c55e', Renewal: '#d97706', Churn: '#b91c1c', Downsell: '#ea580c', DOWNSELL: '#ea580c' };
const EVENT_ICONS = { New: '🆕', Upsell: '⬆️', Renewal: '🔄', Churn: '❌', Downsell: '⬇️', DOWNSELL: '⬇️' };
const eventTypeLabel = (type) => (type === 'Upsell' ? 'Expansion' : type);

function customerName(row) {
  return row.end_user || row.customer || row.bill_to || row.contract_name || 'Unspecified';
}

function productName(row) {
  return row.sub_product || row.product_l2 || row.product || row.product_type || 'Other';
}

function regionName(row) {
  return row.region || row.business_unit || '-';
}

function arrMap(row) {
  return row.arr || row.monthly_arr || {};
}

function changesMap(row) {
  return row.changes || row.monthly_changes || {};
}

function getPrevMonth(month) {
  if (!month) return '';
  const date = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 2, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function changeValue(change, key) {
  const aliases = {
    New: ['New', 'new'],
    Upsell: ['Upsell', 'upsell', 'UpSell', 'up_sell'],
    Renewal: ['Renewal', 'renewal'],
    Churn: ['Churn', 'churn'],
    Downsell: ['Downsell', 'DOWNSELL', 'downsell'],
  }[key] || [key];
  return aliases.reduce((sum, alias) => sum + Number(change?.[alias] || 0), 0);
}

// Exact port of the reference's fS() — was previously dropping the negative
// sign entirely, using the wrong decimal precision for K-values (toFixed(0)
// instead of toFixed(1)), and not adding thousand separators on whole-dollar
// amounts.
function shortMoney(value) {
  const n = Number(value || 0);
  if (!n || Number.isNaN(n)) return '$0';
  const a = Math.abs(n);
  const s = n < 0 ? '-' : '';
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(1)}K`;
  return `${s}$${Math.round(a).toLocaleString()}`;
}

// Matches the reference's own inline convention — e.g. (mom>=0?'+':'')+fS(mom)
// — never abs()-then-resign; shortMoney() already carries its own sign.
function signedShort(value) {
  const n = Number(value || 0);
  return `${n >= 0 ? '+' : ''}${shortMoney(n)}`;
}

// Table MoM cell format: arrow prefix (empty at exactly zero) + magnitude,
// em dash when exactly zero — distinct from signedShort's +/- prefix, which
// the reference only uses for the KPI tile, not the table.
function arrowMom(value, { alwaysArrow = false } = {}) {
  const n = Number(value || 0);
  if (!n) return alwaysArrow ? `▲ ${shortMoney(0)}` : '—';
  return `${n > 0 ? '▲ ' : '▼ '}${shortMoney(Math.abs(n))}`;
}

// Deal-event amount format: '+' prefix for positive, magnitude only for
// negative (no minus sign) — the row's event-type color is what conveys
// direction in the reference, not the number itself.
function eventAmount(value) {
  const n = Number(value || 0);
  return `${n > 0 ? '+' : ''}${shortMoney(Math.abs(n))}`;
}

// Exact port of the reference's fF() — whole-dollar money, no decimals ever
// (fmtFull's 2-decimal Intl currency format doesn't match it). Used inside
// the KPI drill-down modal bodies, which the reference builds with fF().
function fF(value) {
  const n = Number(value || 0);
  return `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;
}

function ChartCard({ title, sub, children, className = '' }) {
  return (
    <div className={`arr-c360-card ${className}`}>
      <h4>{title}</h4>
      {sub && <div className="arr-c360-card-sub">{sub}</div>}
      {children}
    </div>
  );
}

function KpiCardLite({ label, value, sub, color, onClick }) {
  return (
    <div className="arr-c360-kpi" style={{ borderTopColor: color, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <div className="arr-c360-kpi-click">🔍</div>
      <div className="arr-c360-kpi-label">{label}</div>
      <div className="arr-c360-kpi-val" style={{ color }}>{value}</div>
      <div className="arr-c360-kpi-sub">{sub}</div>
    </div>
  );
}

// Matches the reference's rep360OpenModal — same dark navy-gradient-header
// modal as Customer 360's, portaled to <body> for the same navbar-stacking
// fix already applied to that tab and QuotaVsAOP's popup.
function R360KpiModal({ modal, onClose }) {
  if (!modal || typeof document === 'undefined') return null;
  return createPortal(
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 9999, overflowY: 'auto' }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, maxWidth: 680, width: '94%', margin: '60px auto', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,.25)' }}
      >
        <div style={{ background: 'linear-gradient(135deg,#0f2d52,#1a3f6e)', padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>{modal.title}</div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', fontSize: 18, width: 32, height: 32, borderRadius: '50%', cursor: 'pointer' }}
          >×</button>
        </div>
        <div style={{ padding: '20px 22px', maxHeight: '60vh', overflowY: 'auto' }}>{modal.body}</div>
      </div>
    </div>,
    document.body
  );
}

function LineChart({ data, color = '#1e5fa8', height = 250 }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  if (!data.length) return <div className="arr-c360-chart-empty" style={{ height }}>No ARR trend data</div>;
  const width = 760;
  const pad = { l: 58, r: 18, t: 22, b: 38 };
  const vals = data.map(row => Number(row.value || 0));
  const max = Math.max(1, ...vals);
  const xFor = index => pad.l + (index / Math.max(data.length - 1, 1)) * (width - pad.l - pad.r);
  const yFor = value => pad.t + (height - pad.t - pad.b) - (Number(value || 0) / max) * (height - pad.t - pad.b);
  const points = data.map((row, index) => `${xFor(index).toFixed(1)},${yFor(row.value).toFixed(1)}`).join(' ');
  const area = `${pad.l},${height - pad.b} ${points} ${width - pad.r},${height - pad.b}`;
  const ticks = Array.from({ length: 5 }, (_, index) => (max / 4) * index);
  const baseY = height - pad.b;
  const colStep = (width - pad.l - pad.r) / Math.max(1, data.length - 1);

  // Matches the reference's Chart.js tooltip ("ARR: {value} — click for
  // detail") — previously this only had a native <title> on a ~2.5px dot,
  // same fix already applied to Customer 360's equivalent chart.
  const tooltipW = 150;
  const tooltipH = 44;
  const hoverX = hoverIdx != null ? xFor(hoverIdx) : null;
  const tooltipX = hoverX != null ? Math.min(Math.max(hoverX + 10, pad.l), width - tooltipW - 6) : 0;
  const tooltipY = pad.t + 6;

  return (
    <svg className="arr-c360-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" onMouseLeave={() => setHoverIdx(null)}>
      <defs>
        <linearGradient id={`r360Line${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".16" />
          <stop offset="100%" stopColor={color} stopOpacity=".02" />
        </linearGradient>
      </defs>
      {ticks.map((tick, index) => {
        const y = yFor(tick);
        return (
          <g key={index}>
            <line x1={pad.l} x2={width - pad.r} y1={y} y2={y} stroke="rgba(0,0,0,.05)" />
            <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#8898aa">{fmt(tick)}</text>
          </g>
        );
      })}
      <polygon points={area} fill={`url(#r360Line${color.replace('#', '')})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
      {hoverIdx != null && (
        <line x1={hoverX} x2={hoverX} y1={pad.t} y2={baseY} stroke="rgba(0,0,0,.15)" strokeDasharray="3 3" />
      )}
      {data.map((row, index) => (
        <circle
          key={row.month}
          cx={xFor(index)}
          cy={yFor(row.value)}
          r={index === data.length - 1 || index === hoverIdx ? 4.5 : 2.5}
          fill={index === data.length - 1 || index === hoverIdx ? color : `${color}88`}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {data.map((row, index) => (
        <text key={`m-${row.month}`} x={xFor(index)} y={height - 12} textAnchor="middle" fontSize="10" fill="#8898aa">{row.month}</text>
      ))}
      {/* Invisible per-month hit zones so hovering anywhere in a month's
          column (not just the tiny dot) reveals its tooltip. */}
      {data.map((row, index) => (
        <rect key={`hit-${row.month}`} x={xFor(index) - colStep / 2} y={pad.t} width={colStep} height={baseY - pad.t}
          fill="transparent"
          onMouseEnter={() => setHoverIdx(index)}
          onMouseMove={() => setHoverIdx(index)} />
      ))}
      {hoverIdx != null && (
        <g pointerEvents="none">
          <rect x={tooltipX} y={tooltipY} width={tooltipW} height={tooltipH} rx="8" fill="rgba(31,31,31,.92)" />
          <text x={tooltipX + 12} y={tooltipY + 20} fill="#fff" fontSize="12.5" fontWeight="800" fontFamily="Inter, system-ui, sans-serif">
            {data[hoverIdx].month}
          </text>
          <text x={tooltipX + 12} y={tooltipY + 36} fill="#fff" fontSize="11" fontFamily="Inter, system-ui, sans-serif">
            ARR: {shortMoney(data[hoverIdx].value)}
          </text>
        </g>
      )}
    </svg>
  );
}

// Donut-wedge path helpers — same pattern as ARRDashboard.js's BUChart /
// SubProductDonut and Customer360.js's (now-fixed) Product Mix donut.
function polarPoint(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function donutSlicePath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const sweep = endAngle - startAngle;
  // A single customer/product occupies exactly 360°. SVG cannot draw a
  // complete circle with one arc whose start and end points are identical,
  // so render the ring as two 180° arcs in that case.
  if (sweep >= 359.999) {
    const midAngle = startAngle + 180;
    const outerStart = polarPoint(cx, cy, rOuter, startAngle);
    const outerMid = polarPoint(cx, cy, rOuter, midAngle);
    const outerEnd = polarPoint(cx, cy, rOuter, endAngle);
    const innerEnd = polarPoint(cx, cy, rInner, endAngle);
    const innerMid = polarPoint(cx, cy, rInner, midAngle);
    const innerStart = polarPoint(cx, cy, rInner, startAngle);
    return [
      `M ${outerStart.x} ${outerStart.y}`,
      `A ${rOuter} ${rOuter} 0 0 1 ${outerMid.x} ${outerMid.y}`,
      `A ${rOuter} ${rOuter} 0 0 1 ${outerEnd.x} ${outerEnd.y}`,
      `L ${innerEnd.x} ${innerEnd.y}`,
      `A ${rInner} ${rInner} 0 0 0 ${innerMid.x} ${innerMid.y}`,
      `A ${rInner} ${rInner} 0 0 0 ${innerStart.x} ${innerStart.y}`,
      'Z',
    ].join(' ');
  }

  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  const outerStart = polarPoint(cx, cy, rOuter, startAngle);
  const outerEnd = polarPoint(cx, cy, rOuter, endAngle);
  const innerEnd = polarPoint(cx, cy, rInner, endAngle);
  const innerStart = polarPoint(cx, cy, rInner, startAngle);
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

// Rebuilt as an SVG-wedge donut (was a stroke-dasharray circle with only a
// flat native <title>) to match the polished pattern used elsewhere, with a
// stable untransformed hit-target underneath the animated pop-out wedge so
// hovering can't shift the wedge out from under the cursor. `onSelect`, when
// passed, makes wedges/legend rows clickable — matches the reference's
// Customer Mix donut (click → jump to Customer 360); Product Mix has no
// onSelect, matching the reference (it's hover-only there).
function DonutChart({ items, empty = 'No data', onSelect }) {
  const rows = items.filter(item => Number(item.value || 0) > 0).sort((a, b) => b.value - a.value);
  const total = rows.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const [hoverIdx, setHoverIdx] = useState(null);

  if (!rows.length || !total) return <div className="arr-c360-chart-empty">{empty}</div>;

  let cumAngle = 0;
  const segments = rows.map((row, i) => {
    const pctVal = (Number(row.value || 0) / total) * 100;
    const startAngle = cumAngle;
    cumAngle += (pctVal / 100) * 360;
    return { row, i, startAngle, endAngle: cumAngle, midAngle: (startAngle + cumAngle) / 2, pctVal };
  });

  const CX = 100;
  const CY = 100;
  const R_OUTER = 82;
  const R_INNER = 50;
  const POP = 8;
  const active = hoverIdx != null ? segments[hoverIdx] : null;

  return (
    <div className="arr-prod-donut-layout" onMouseLeave={() => setHoverIdx(null)}>
      <div className="arr-bu-donut-stage">
        <svg viewBox="0 0 200 200" className="arr-bu-donut-svg">
          {segments.map((seg) => {
            const isActive = hoverIdx === seg.i;
            const midRad = ((seg.midAngle - 90) * Math.PI) / 180;
            const dx = isActive ? Math.cos(midRad) * POP : 0;
            const dy = isActive ? Math.sin(midRad) * POP : 0;
            const color = PALETTE[seg.i % PALETTE.length];
            const title = onSelect
              ? `${seg.row.label}: ${shortMoney(seg.row.value)} — click to open Customer 360`
              : `${seg.row.label}: ${shortMoney(seg.row.value)} (${seg.pctVal.toFixed(1)}%)`;
            // A single 100%-share segment has identical start/end points,
            // which SVG renders as an invisible zero-length arc — split it
            // into two 180° arcs instead so a lone full-share category still
            // draws a complete ring.
            const isFullCircle = seg.endAngle - seg.startAngle >= 359.99;
            const wedges = isFullCircle
              ? [[seg.startAngle, seg.startAngle + 180], [seg.startAngle + 180, seg.endAngle]]
              : [[seg.startAngle, seg.endAngle]];
            return (
              <g key={seg.row.label}>
                {wedges.map(([a0, a1], wi) => {
                  const path = donutSlicePath(CX, CY, R_OUTER, R_INNER, a0, a1);
                  return (
                    <g key={wi}>
                      <path
                        d={path}
                        className={`arr-bu-donut-slice${isActive ? ' active' : ''}`}
                        fill={color}
                        style={{
                          stroke: color,
                          transform: `translate(${dx}px, ${dy}px)`,
                          opacity: hoverIdx == null || isActive ? 1 : 0.55,
                          pointerEvents: 'none',
                        }}
                      />
                      <path
                        d={path}
                        fill="transparent"
                        style={{ cursor: onSelect ? 'pointer' : 'default' }}
                        tabIndex={0}
                        role="button"
                        onMouseEnter={() => setHoverIdx(seg.i)}
                        onFocus={() => setHoverIdx(seg.i)}
                        onClick={onSelect ? () => onSelect(seg.row.label) : undefined}
                        aria-label={title}
                      >
                        <title>{title}</title>
                      </path>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
        <div className="arr-bu-donut-center">
          {active ? (
            <>
              <div className="arr-bu-donut-center-label">{active.row.label}</div>
              <div className="arr-bu-donut-center-val" style={{ color: PALETTE[active.i % PALETTE.length] }}>{fmt(active.row.value)}</div>
              <div className="arr-bu-donut-center-pct">{active.pctVal.toFixed(1)}% of total</div>
            </>
          ) : (
            <>
              <div className="arr-bu-donut-center-label">Total</div>
              <div className="arr-bu-donut-center-val">{fmt(total)}</div>
            </>
          )}
        </div>
      </div>
      <div className="arr-prod-legend">
        {segments.map((seg) => (
          <button
            key={seg.row.label}
            type="button"
            className={hoverIdx === seg.i ? 'active' : ''}
            style={{ cursor: onSelect ? 'pointer' : 'default' }}
            onMouseEnter={() => setHoverIdx(seg.i)}
            onFocus={() => setHoverIdx(seg.i)}
            onClick={onSelect ? () => onSelect(seg.row.label) : undefined}
          >
            <i style={{ background: PALETTE[seg.i % PALETTE.length] }} />{seg.row.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Matches the reference's Chart.js bar chart exactly: New+Upsell and
// Churn+Downsell are two series on the SAME baseline, with Churn+Downsell
// plotted as a NEGATIVE value — so it extends downward below a zero line,
// not stacked upward alongside the win bar. Previously both series grew
// upward from a shared bottom edge, which reads as "total activity" rather
// than the reference's actual win-vs-loss diverging shape.
function WaterfallBars({ data, onBarClick }) {
  if (!data.length) return <div className="arr-c360-chart-empty">No bookings data</div>;
  const yMax = Math.max(...data.map(row => Math.max(row.newWon, row.loss)), 1);
  const PAD_L = 46;
  const PAD_R = 16;
  const PAD_T = 16;
  const PAD_B = 32;
  const SVG_H = 260;
  const CHART_H = SVG_H - PAD_T - PAD_B;
  const zeroY = PAD_T + CHART_H / 2;
  const n = data.length;
  const MIN_SLOT = 56;
  const SVG_W = Math.max(460, PAD_L + PAD_R + n * MIN_SLOT);
  const slot = (SVG_W - PAD_L - PAD_R) / n;
  const barW = Math.min(30, Math.max(14, slot * 0.5));
  const toPosY = v => zeroY - (Number(v || 0) / yMax) * (CHART_H / 2);
  const toNegY = v => zeroY + (Number(v || 0) / yMax) * (CHART_H / 2);
  const ticks = [1, 0.5, 0, -0.5, -1].map(t => ({ v: yMax * t, y: zeroY - t * (CHART_H / 2) }));

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ width: '100%', minWidth: SVG_W, height: SVG_H, display: 'block' }}>
        {ticks.map((tick, i) => (
          <g key={i}>
            <line x1={PAD_L} x2={SVG_W - PAD_R} y1={tick.y} y2={tick.y} stroke={tick.v === 0 ? '#e2e8f0' : 'rgba(0,0,0,.04)'} strokeWidth="1" />
            <text x={PAD_L - 8} y={tick.y + 3.5} textAnchor="end" fontSize="9" fill="#8898aa" fontFamily="Inter, sans-serif">
              {fmt(Math.abs(tick.v))}
            </text>
          </g>
        ))}
        {data.map((row, i) => {
          const x = PAD_L + i * slot + (slot - barW) / 2;
          const wonTop = toPosY(row.newWon);
          const lossBottom = toNegY(row.loss);
          return (
            <g key={row.month}>
              <rect x={x} y={wonTop} width={barW} height={Math.max(0, zeroY - wonTop)}
                fill="#15803dcc" stroke="#15803d" strokeWidth="1" rx="3"
                role={onBarClick ? 'button' : undefined} tabIndex={onBarClick ? '0' : undefined}
                style={{ cursor: onBarClick ? 'pointer' : 'default' }}
                onClick={onBarClick ? event => { event.stopPropagation(); onBarClick(row.month, 'won'); } : undefined}
                onKeyDown={onBarClick ? event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onBarClick(row.month, 'won'); } } : undefined}>
                <title>{`${row.month} New+Expansion: ${shortMoney(row.newWon)}${onBarClick ? ' · click for deals' : ''}`}</title>
              </rect>
              <rect x={x} y={zeroY} width={barW} height={Math.max(0, lossBottom - zeroY)}
                fill="#b91c1ccc" stroke="#b91c1c" strokeWidth="1" rx="3"
                role={onBarClick ? 'button' : undefined} tabIndex={onBarClick ? '0' : undefined}
                style={{ cursor: onBarClick ? 'pointer' : 'default' }}
                onClick={onBarClick ? event => { event.stopPropagation(); onBarClick(row.month, 'loss'); } : undefined}
                onKeyDown={onBarClick ? event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onBarClick(row.month, 'loss'); } } : undefined}>
                <title>{`${row.month} Churn+Downsell: ${shortMoney(row.loss)}${onBarClick ? ' · click for deals' : ''}`}</title>
              </rect>
              <text x={x + barW / 2} y={SVG_H - 12} textAnchor="middle" fontSize="9" fill="#8898aa" fontFamily="Inter, sans-serif" transform={`rotate(-32 ${x + barW / 2} ${SVG_H - 12})`}>
                {row.month}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="arr-c360-legend">
        <span><i style={{ background: '#15803d' }} />New+Expansion</span>
        <span><i style={{ background: '#b91c1c' }} />Churn+Downsell</span>
      </div>
    </div>
  );
}

function Rep360Tab({ data, onSelectCustomer, focusRep }) {
  const [selectedRep, setSelectedRep] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('arr');
  const [sortAsc, setSortAsc] = useState(false);
  const [kpiModal, setKpiModal] = useState(null);

  // Rep 360 shares the same server-filtered `data` as every other tab, so the
  // global Business Unit / Sales Rep / Product / Line of Business filters
  // apply here too (only the reps/customers matching the active filter show up).
  const records = data.records || EMPTY_ARRAY;

  // Jump straight to a rep's detail when arriving via a bar click elsewhere
  // in the dashboard (e.g. "ARR by Sales Rep"), same as clicking a bar in the
  // reference tool's chart selects that rep in the Rep 360 tab. Keyed on
  // token (not just name) so clicking the same bar twice in a row still
  // re-selects it even if the dropdown was changed in between.
  useEffect(() => {
    if (focusRep?.name) setSelectedRep(focusRep.name);
  }, [focusRep]);

  const base = useMemo(() => {
    const monthSet = new Set(data.period?.months || data.all_months || []);
    records.forEach(row => {
      Object.keys(arrMap(row)).forEach(month => monthSet.add(month));
      Object.keys(changesMap(row)).forEach(month => monthSet.add(month));
    });
    const allMonths = [...monthSet].filter(Boolean).sort();
    const latest = data.period?.to
      || data.period?.latest_month
      || data.latest_month
      || data.kpis?.latest_month
      || allMonths.filter(month => records.some(row => Number(arrMap(row)[month] || 0) > 0)).at(-1)
      || allMonths.at(-1)
      || '';
    const periodFrom = data.period?.from || latest;
    const periodMonths = allMonths.filter(month => month >= periodFrom && month <= latest);
    const repActivity = {};
    const repCurr = {};

    records.forEach(row => {
      const rep = row.sales_person || row.sales_rep || '';
      if (!rep) return;
      repCurr[rep] = (repCurr[rep] || 0) + Number(arrMap(row)[latest] || 0);
      const hasPeriodArr = periodMonths.some(month => Number(arrMap(row)[month] || 0) > 0);
      const hasPeriodEvent = periodMonths.some(month =>
        Object.values(changesMap(row)[month] || {}).some(value => Number(value || 0) !== 0)
      );
      if (hasPeriodArr || hasPeriodEvent) repActivity[rep] = true;
    });

    const reps = Object.keys(repActivity)
      .sort((a, b) => (repCurr[b] || 0) - (repCurr[a] || 0) || a.localeCompare(b))
      .map(rep => ({ rep, arr: repCurr[rep] || 0 }));

    return { allMonths, latest, periodFrom, periodMonths, reps };
  }, [data, records]);

  const rep = selectedRep || base.reps[0]?.rep || '';

  useEffect(() => {
    if (selectedRep && !base.reps.some(item => item.rep === selectedRep)) {
      setSelectedRep(base.reps[0]?.rep || '');
    }
  }, [base.reps, selectedRep]);

  const detail = useMemo(() => {
    if (!rep) return null;
    const deals = records.filter(row => (row.sales_person || row.sales_rep || '') === rep);
    if (!deals.length) return null;
    // Reference's buildRep360 uses filterTo||D.latest_month for "latest" (so
    // the active period filter's To date drives every stat here), and bounds
    // periodMonths by filterFrom too — previously neither was wired in, so
    // this tab silently ignored the shared period filter entirely.
    const periodFrom = data.period?.from || base.periodFrom || '';
    const latest = data.period?.to || base.latest;
    const prevM = getPrevMonth(latest);
    const openingMonth = getPrevMonth(periodFrom || latest);
    const periodMonths = base.allMonths.filter(month => (!periodFrom || month >= periodFrom) && month <= latest);
    const chartMonths = periodMonths.slice(-12);
    const periodMonthSet = new Set(periodMonths);

    let totalArr = 0;
    let prevArr = 0;
    let openingArr = 0;
    let periodNew = 0;
    let periodUp = 0;
    let periodChurn = 0;
    let periodDn = 0;
    const custArr = {};
    const custPrev = {};
    const prodArr = {};
    const monthlyArr = {};
    const monthlyNew = {};
    const monthlyLoss = {};
    const periodMovement = {};
    const lineMap = {};
    const events = [];

    deals.forEach(row => {
      const customer = customerName(row);
      const product = productName(row);
      const region = regionName(row);
      const monthly = arrMap(row);
      const changes = changesMap(row);
      const arr = Number(monthly[latest] || 0);
      const prev = Number(monthly[prevM] || 0);
      const opening = Number(monthly[openingMonth] || 0);
      totalArr += arr;
      prevArr += prev;
      openingArr += opening;
      custPrev[customer] = (custPrev[customer] || 0) + prev;
      // Net all positive and negative/correction rows before building the
      // mix charts. Dropping negative rows made the breakdown totals exceed
      // Closing ARR (for example, $679.4K vs Hardeep's net $662.6K).
      custArr[customer] = (custArr[customer] || 0) + arr;
      prodArr[product] = (prodArr[product] || 0) + arr;
      chartMonths.forEach(month => {
        monthlyArr[month] = (monthlyArr[month] || 0) + Number(monthly[month] || 0);
      });
      periodMonths.forEach(month => {
        const change = changes[month] || {};
        if (!periodMovement[month]) periodMovement[month] = { new: 0, upsell: 0, churn: 0, downsell: 0 };
        periodMovement[month].new += changeValue(change, 'New');
        periodMovement[month].upsell += changeValue(change, 'Upsell');
        periodMovement[month].churn += changeValue(change, 'Churn');
        periodMovement[month].downsell += changeValue(change, 'Downsell');
      });
      chartMonths.forEach(month => {
        const change = changes[month] || {};
        const won = Math.max(0, changeValue(change, 'New')) + Math.max(0, changeValue(change, 'Upsell'));
        const loss = Math.abs(Math.min(0, changeValue(change, 'Churn'))) + Math.abs(Math.min(0, changeValue(change, 'Downsell')));
        monthlyNew[month] = (monthlyNew[month] || 0) + won;
        monthlyLoss[month] = (monthlyLoss[month] || 0) + loss;
      });
      periodMonths.forEach(month => {
        const change = changes[month] || {};
        const key = `${customer}||${product}`;
        if (!lineMap[key]) lineMap[key] = { customer, region, product, arr: 0, prev: 0, periodNew: 0 };
        lineMap[key].periodNew += Math.max(0, changeValue(change, 'New')) + Math.max(0, changeValue(change, 'Upsell'));
      });
      const key = `${customer}||${product}`;
      if (!lineMap[key]) lineMap[key] = { customer, region, product, arr: 0, prev: 0, periodNew: 0 };
      lineMap[key].arr += arr;
      lineMap[key].prev += prev;

      Object.entries(changes).forEach(([month, change]) => {
        if (!periodMonthSet.has(month)) return;
        Object.entries({
          New: changeValue(change, 'New'),
          Upsell: changeValue(change, 'Upsell'),
          Renewal: changeValue(change, 'Renewal'),
          Churn: changeValue(change, 'Churn'),
          Downsell: changeValue(change, 'Downsell'),
        }).forEach(([type, amount]) => {
          if (!amount) return;
          events.push({ month, type, amount, customer, product });
        });
      });
    });

    periodMonths.forEach(month => {
      const movement = periodMovement[month] || {};
      periodNew += Math.max(0, Number(movement.new || 0));
      periodUp += Math.max(0, Number(movement.upsell || 0));
      periodChurn += Math.abs(Math.min(0, Number(movement.churn || 0)));
      periodDn += Math.abs(Math.min(0, Number(movement.downsell || 0)));
      monthlyNew[month] = Math.max(0, Number(movement.new || 0))
        + Math.max(0, Number(movement.upsell || 0));
      monthlyLoss[month] = Math.abs(Math.min(0, Number(movement.churn || 0)))
        + Math.abs(Math.min(0, Number(movement.downsell || 0)));
    });

    const mom = totalArr - prevArr;
    const logos = Object.values(custArr).filter(value => Number(value) > 0).length;
    const acv = logos > 0 ? totalArr / logos : 0;
    const nrr = openingArr > 0 ? ((openingArr - periodChurn - periodDn + periodUp) / openingArr) * 100 : 0;
    const regions = [...new Set(deals.map(regionName).filter(value => value && value !== '-'))];
    const region = regions.length === 1 ? regions[0] : regions.length > 1 ? `${regions.length} BUs` : '-';

    const customerMix = Object.entries(custArr)
      .filter(([, value]) => Number(value) > 0)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
    const productMix = Object.entries(prodArr)
      .filter(([, value]) => Number(value) > 0)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
    const activeContracts = new Set(
      deals
        .filter(row => periodMonths.some(month =>
          Number(arrMap(row)[month] || 0) !== 0
          || Object.values(changesMap(row)[month] || {}).some(value => Number(value || 0) !== 0)
        ))
        .map(row => row.contract_id || row.key_id)
        .filter(Boolean)
    ).size;
    const trend = chartMonths.map(month => ({ month, value: monthlyArr[month] || 0 }));
    const waterfall = chartMonths.map(month => ({ month, newWon: monthlyNew[month] || 0, loss: monthlyLoss[month] || 0 }));
    const rows = Object.values(lineMap)
      .map(row => ({ ...row, mom: row.arr - row.prev }))
      .filter(row => row.arr > 0 || row.periodNew > 0);
    events.sort((a, b) => b.month.localeCompare(a.month));

    let insight = null;
    if (mom < 0 && prevArr > 0 && Math.abs(mom) / prevArr > 0.05) {
      insight = { tone: 'warn', text: `${rep} ARR declined ${shortMoney(Math.abs(mom))} MoM - investigate churn or downsell` };
    } else if (periodChurn + periodDn > periodNew + periodUp) {
      insight = { tone: 'danger', text: `${rep} has more churn/downsell (${shortMoney(periodChurn + periodDn)}) than new bookings (${shortMoney(periodNew + periodUp)}) this period` };
    } else if (nrr > 110) {
      insight = { tone: 'good', text: `${rep} is a top performer - NRR ${nrr.toFixed(1)}% with ${shortMoney(periodNew + periodUp)} in new bookings` };
    }

    return {
      deals,
      latest,
      prevM,
      openingMonth,
      periodMonths,
      region,
      totalArr,
      prevArr,
      openingArr,
      mom,
      periodNew,
      periodUp,
      periodChurn,
      periodDn,
      nrr,
      acv,
      logos,
      activeContracts,
      trend,
      customerMix,
      productMix,
      waterfall,
      rows,
      events,
      insight,
    };
  }, [rep, records, base, data.period?.from, data.period?.to]);

  const filteredRows = useMemo(() => {
    if (!detail) return [];
    const q = search.trim().toLowerCase();
    const rows = detail.rows
      .filter(row => !q || row.customer.toLowerCase().includes(q) || row.product.toLowerCase().includes(q))
      .map(row => ({ ...row }));
    rows.sort((a, b) => {
      const av = sortKey === 'cust' ? a.customer : sortKey === 'new' ? a.periodNew : a.arr;
      const bv = sortKey === 'cust' ? b.customer : sortKey === 'new' ? b.periodNew : b.arr;
      if (typeof av === 'string') return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortAsc ? av - bv : bv - av;
    });
    return rows;
  }, [detail, search, sortAsc, sortKey]);

  const setSort = (key) => {
    if (sortKey === key) setSortAsc(prev => !prev);
    else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const totals = filteredRows.reduce((acc, row) => ({
    arr: acc.arr + row.arr,
    periodNew: acc.periodNew + row.periodNew,
    mom: acc.mom + row.mom,
  }), { arr: 0, periodNew: 0, mom: 0 });

  // Matches the reference's rep360KpiClick exactly: only 'bookings', 'nrr',
  // 'arr' and 'acv' get a real drill-down table — 'mom' is wired up (the KPI
  // tile is clickable) but has no case in the reference either, so it falls
  // through to the same generic placeholder text.
  const openKpiModal = (type) => {
    if (!detail) return;
    const rowStyle = { borderBottom: '1px solid #f1f5f9' };
    const tdLabel = { padding: '9px 12px', fontWeight: 600 };
    const tdMoney = { padding: '9px 12px', textAlign: 'right', fontFamily: '"Courier New", monospace', fontWeight: 700 };
    let body;
    if (type === 'bookings') {
      const bookings = [];
      detail.deals.forEach((row) => {
        detail.periodMonths.forEach((month) => {
          const change = changesMap(row)[month] || {};
          ['New', 'Upsell'].forEach((t) => {
            const amount = changeValue(change, t);
            if (amount > 0) bookings.push({ month, type: t === 'Upsell' ? 'Expansion' : t, customer: customerName(row), product: productName(row), amount });
          });
        });
      });
      bookings.sort((a, b) => b.amount - a.amount);
      body = (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#f8fafd' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Month</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Customer</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Product</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Amount</th>
          </tr></thead>
          <tbody>
            {bookings.map((b, i) => (
              <tr key={`${b.month}-${b.customer}-${b.type}-${i}`} style={rowStyle}>
                <td style={{ padding: '8px 12px', fontSize: 11 }}>{b.month}</td>
                <td style={{ padding: '8px 12px', fontWeight: 600 }}>{b.customer}</td>
                <td style={{ padding: '8px 12px', fontSize: 11, color: '#718096' }}>{b.product}</td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{ background: b.type === 'New' ? '#dbeafe' : '#dcfce7', color: b.type === 'New' ? '#1e5fa8' : '#15803d', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{b.type}</span>
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: '"Courier New", monospace', fontWeight: 700, color: '#15803d' }}>{fF(b.amount)}</td>
              </tr>
            ))}
            {!bookings.length && <tr><td colSpan={5} style={{ padding: 14, textAlign: 'center', color: '#94a3b8' }}>No bookings this period</td></tr>}
          </tbody>
        </table>
      );
    } else if (type === 'nrr') {
      const rows = [
        [`Opening ARR (${detail.openingMonth})`, fF(detail.openingArr), '#718096'],
        ['+ Expansion', fF(detail.periodUp), '#15803d'],
        ['- Churn', fF(-detail.periodChurn), '#b91c1c'],
        ['- Downsell', fF(-detail.periodDn), '#ea580c'],
        ['Closing ARR', fF(detail.totalArr), '#0f172a'],
        ['= NRR', `${detail.nrr.toFixed(1)}%`, '#1e5fa8'],
      ];
      body = (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {rows.map(([label, value, color]) => (
              <tr key={label} style={rowStyle}>
                <td style={tdLabel}>{label}</td>
                <td style={{ ...tdMoney, color }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else if (type === 'arr') {
      const custProd = {};
      detail.deals.forEach((row) => {
        const v = Number(arrMap(row)[detail.latest] || 0);
        const key = customerName(row);
        if (!custProd[key]) custProd[key] = { total: 0, products: {} };
        custProd[key].total += v;
        const product = productName(row);
        custProd[key].products[product] = (custProd[key].products[product] || 0) + v;
      });
      const custRows = Object.entries(custProd)
        .filter(([, info]) => Number(info.total) > 0)
        .sort((a, b) => b[1].total - a[1].total);
      body = (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#f8fafd' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Customer</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Products</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>ARR</th>
          </tr></thead>
          <tbody>
            {custRows.map(([customer, info]) => (
              <tr
                key={customer}
                style={{ ...rowStyle, cursor: onSelectCustomer ? 'pointer' : 'default' }}
                onClick={onSelectCustomer ? () => onSelectCustomer(customer) : undefined}
              >
                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e5fa8', textDecoration: 'underline' }}>{customer}</td>
                <td style={{ padding: '8px 12px', fontSize: 11, color: '#718096' }}>
                  {Object.entries(info.products).filter(([, value]) => Number(value) > 0).map(([product]) => product).join(', ')}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: '"Courier New", monospace', fontWeight: 700, color: '#1e5fa8' }}>{fF(info.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else if (type === 'acv') {
      body = (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#f8fafd' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Customer</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>ARR</th>
          </tr></thead>
          <tbody>
            {detail.customerMix.map((item) => (
              <tr
                key={item.label}
                style={{ ...rowStyle, cursor: onSelectCustomer ? 'pointer' : 'default' }}
                onClick={onSelectCustomer ? () => onSelectCustomer(item.label) : undefined}
              >
                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e5fa8', textDecoration: 'underline' }}>{item.label}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: '"Courier New", monospace', fontWeight: 700, color: '#1e5fa8' }}>{fF(item.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else {
      body = <div style={{ padding: 20, color: '#718096', textAlign: 'center' }}>{type} detail coming soon</div>;
    }
    setKpiModal({ title: `${type.charAt(0).toUpperCase()}${type.slice(1)} — ${rep}`, body });
  };

  const openWaterfallMonth = (month, kind) => {
    if (!detail) return;
    const rowStyle = { borderBottom: '1px solid #f1f5f9' };
    const wonTypes = ['New', 'Upsell'];
    const lossTypes = ['Churn', 'Downsell'];
    const types = kind === 'won' ? wonTypes : lossTypes;
    const badgeColors = {
      New: ['#dbeafe', '#1e5fa8'], Upsell: ['#dcfce7', '#15803d'],
      Churn: ['#fee2e2', '#b91c1c'], Downsell: ['#ffedd5', '#c2410c'],
    };
    const rows = detail.events
      .filter(event => event.month === month && types.includes(event.type) && (kind === 'won' ? event.amount > 0 : event.amount < 0))
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    const body = (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ background: '#f8fafd' }}>
          <th style={{ padding: '8px 12px', textAlign: 'left' }}>Customer</th>
          <th style={{ padding: '8px 12px', textAlign: 'left' }}>Product</th>
          <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
          <th style={{ padding: '8px 12px', textAlign: 'right' }}>Amount</th>
        </tr></thead>
        <tbody>
          {rows.map((event, i) => {
            const [bg, color] = badgeColors[event.type] || ['#f1f5f9', '#475569'];
            return (
              <tr key={`${event.customer}-${event.type}-${i}`} style={{ ...rowStyle, cursor: onSelectCustomer ? 'pointer' : 'default' }} onClick={onSelectCustomer ? () => onSelectCustomer(event.customer) : undefined}>
                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e5fa8', textDecoration: onSelectCustomer ? 'underline' : 'none' }}>{event.customer}</td>
                <td style={{ padding: '8px 12px', fontSize: 11, color: '#718096' }}>{event.product}</td>
                <td style={{ padding: '8px 12px' }}><span style={{ background: bg, color, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{event.type}</span></td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: '"Courier New", monospace', fontWeight: 700, color: kind === 'won' ? '#15803d' : '#b91c1c' }}>{fF(Math.abs(event.amount))}</td>
              </tr>
            );
          })}
          {!rows.length && <tr><td colSpan={4} style={{ padding: 14, textAlign: 'center', color: '#94a3b8' }}>No {kind === 'won' ? 'new/expansion' : 'churn/downsell'} events for {month}</td></tr>}
        </tbody>
      </table>
    );
    setKpiModal({ title: `${month} — ${kind === 'won' ? 'New + Expansion' : 'Churn + Downsell'} — ${rep}`, body });
  };

  return (
    <div className="arr-rep360">
      <R360KpiModal modal={kpiModal} onClose={() => setKpiModal(null)} />
      <DevOverlay name="Rep360 Selector">
        <div className="arr-r360-selector">
          <div className="arr-r360-select-wrap">
            <label>Select Sales Rep</label>
            <select value={rep} onChange={event => setSelectedRep(event.target.value)}>
              <option value="">- Choose a Sales Rep -</option>
              {base.reps.map(item => (
                <option key={item.rep} value={item.rep}>
                  {item.rep} ({item.arr > 0 ? `${fmt(item.arr)} ARR` : 'no active ARR'})
                </option>
              ))}
            </select>
          </div>
          {detail && (
            <div className="arr-c360-badges">
              <span className="arr-tag">{detail.region}</span>
            </div>
          )}
        </div>
      </DevOverlay>

      {!rep && (
        <div className="arr-c360-empty">
          <div className="arr-c360-empty-icon">🧑‍💼</div>
          <h3>Select a Sales Rep above</h3>
          <p>Or click any bar in the ARR by Sales Rep chart on the Dashboard tab</p>
        </div>
      )}

      {rep && !detail && (
        <div className="arr-c360-empty">
          <h3>No data for {rep}</h3>
        </div>
      )}

      {detail && (
        <>
          <div className="arr-c360-kpis">
            <DevOverlay name="Rep360 Closing ARR">
              <KpiCardLite label="Closing ARR" value={shortMoney(detail.totalArr)} sub={`as of ${detail.latest}`} color="#1e5fa8" onClick={() => openKpiModal('arr')} />
            </DevOverlay>
            <DevOverlay name="Rep360 MoM Change">
              <KpiCardLite label="MoM Change" value={signedShort(detail.mom)} sub={`vs ${detail.prevM}`} color={detail.mom >= 0 ? '#15803d' : '#b91c1c'} onClick={() => openKpiModal('mom')} />
            </DevOverlay>
            <DevOverlay name="Rep360 Period Bookings">
              <KpiCardLite label="Period Bookings" value={shortMoney(detail.periodNew + detail.periodUp)} sub={`${data.period?.from} → ${detail.latest} · new + expansion`} color="#059669" onClick={() => openKpiModal('bookings')} />
            </DevOverlay>
            <DevOverlay name="Rep360 NRR">
              <KpiCardLite label="NRR %" value={`${detail.nrr.toFixed(1)}%`} sub={`from ${detail.openingMonth} opening ARR`} color={detail.nrr >= 100 ? '#15803d' : detail.nrr >= 80 ? '#d97706' : '#b91c1c'} onClick={() => openKpiModal('nrr')} />
            </DevOverlay>
            <DevOverlay name="Rep360 Avg Contract">
              <KpiCardLite label="Avg Contract" value={shortMoney(detail.acv)} sub={`${detail.logos} accounts`} color="#6d28d9" onClick={() => openKpiModal('acv')} />
            </DevOverlay>
          </div>

          {detail.insight && <div className={`arr-c360-insight ${detail.insight.tone}`}>{detail.insight.text}</div>}

          <div className="arr-c360-grid2">
            <DevOverlay name="Rep360 ARR Trend">
              <ChartCard title="ARR Trend — Selected Period" sub={`${data.period?.from} to ${detail.latest} · showing up to 12 months · ${rep}`}>
                <LineChart data={detail.trend} color="#1e5fa8" height={250} />
              </ChartCard>
            </DevOverlay>
            <DevOverlay name="Rep360 Customer Mix">
              <ChartCard title="Customer Mix" sub="Current ARR split by customer">
                <DonutChart items={detail.customerMix} empty="No customer ARR" onSelect={onSelectCustomer} />
              </ChartCard>
            </DevOverlay>
          </div>

          <div className="arr-c360-grid2">
            <DevOverlay name="Rep360 Waterfall">
              <ChartCard title="New Won vs Churn — Selected Period ↗" sub={`${data.period?.from} to ${detail.latest} · showing up to 12 months · Click a bar for that month's deals`}>
                <WaterfallBars data={detail.waterfall} onBarClick={openWaterfallMonth} />
              </ChartCard>
            </DevOverlay>
            <DevOverlay name="Rep360 Product Mix">
              <ChartCard title="ARR by Product" sub="Current ARR split by product">
                <DonutChart items={detail.productMix} empty="No product ARR" />
              </ChartCard>
            </DevOverlay>
          </div>

          <DevOverlay name="Rep360 Customer Detail">
            <div className="arr-tcard arr-r360-table-card">
              <div className="arr-tcard-hdr">
                <span className="arr-tcard-title">👤 Customer Detail</span>
                <input className="arr-search" placeholder="🔍 Search customers..." value={search} onChange={event => setSearch(event.target.value)} />
              </div>
              <div className="arr-twrap">
                <table className="arr-r360-table">
                  <thead>
                    <tr>
                      <th onClick={() => setSort('cust')}>Customer ↕</th>
                      <th>BU</th>
                      <th>Product</th>
                      <th className="right" onClick={() => setSort('arr')}>Closing ARR ↕</th>
                      <th className="right" onClick={() => setSort('new')}>Period New+Expansion ↕</th>
                      <th className="right">MoM</th>
                      <th className="center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(row => {
                      const growing = row.arr > 0 && row.mom > 0;
                      const declining = row.arr > 0 && row.mom < 0;
                      return (
                        <tr key={`${row.customer}-${row.product}`}>
                          <td><strong>{row.customer}</strong></td>
                          <td><span className="arr-tag">{row.region}</span></td>
                          <td>{row.product}</td>
                          <td className="right money">{row.arr ? shortMoney(row.arr) : '—'}</td>
                          <td className="right money arr-up">{row.periodNew ? shortMoney(row.periodNew) : '—'}</td>
                          <td className={`right money ${row.mom > 0 ? 'arr-up' : row.mom < 0 ? 'arr-dn' : ''}`}>{arrowMom(row.mom)}</td>
                          <td className="center">
                            <span className={`arr-r360-status ${growing ? 'growing' : declining ? 'declining' : 'stable'}`}>
                              {growing ? '▲ Growing' : declining ? '▼ Declining' : '━ Stable'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {!filteredRows.length && <tr><td colSpan={7} className="bm-empty-row">No customer data for this rep.</td></tr>}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>Total ({filteredRows.length} lines)</td>
                      <td className="right money">{shortMoney(totals.arr)}</td>
                      <td className="right money arr-up">{shortMoney(totals.periodNew)}</td>
                      <td className={`right money ${totals.mom >= 0 ? 'arr-up' : 'arr-dn'}`}>{arrowMom(totals.mom, { alwaysArrow: true })}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </DevOverlay>

          <DevOverlay name="Rep360 Events Timeline">
            <ChartCard title="📅 Deal Events Timeline" sub={`Events in selected period · ${data.period?.from} to ${detail.latest}`} className="arr-c360-card-spaced">
              <div className="arr-c360-table-scroll short">
                <table className="arr-c360-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Event</th>
                      <th>Product</th>
                      <th className="right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.events.slice(0, 50).map((event, index) => {
                      const color = EVENT_COLORS[event.type] || '#64748b';
                      return (
                        <tr key={`${event.month}-${event.customer}-${event.type}-${index}`}>
                          <td><strong>{event.month}</strong></td>
                          <td><strong>{event.customer}</strong></td>
                          <td><span className="arr-c360-event" style={{ background: `${color}18`, color }}>{EVENT_ICONS[event.type] || '•'} {eventTypeLabel(event.type)}</span></td>
                          <td>{event.product}</td>
                          <td className="right money" style={{ color }}>{eventAmount(event.amount)}</td>
                        </tr>
                      );
                    })}
                    {!detail.events.length && <tr><td colSpan={5} className="bm-empty-row">No deal events found</td></tr>}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </DevOverlay>
        </>
      )}
    </div>
  );
}

export { Rep360Tab };
